import React, { useMemo } from "react"
import { Select, Tag } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import {
  useDashboardStore,
  selectAnyFilterActive,
} from "features/Dashboard/runtime/dashboardStore"
import { getBoolChildLabels } from "utils/codebookUtils"

const lastSegment = (p: string): string => p.split("//").pop() ?? p

/** A select's value as a selection: none when cleared, one when single. */
const toValues = (v: string | string[] | undefined | null): string[] =>
  v === undefined || v === null ? [] : Array.isArray(v) ? v : [v]

/**
 * The viewer's filters: one select per dashboard filter, a chip per active
 * cross-filter (from clicking a bar, slice or cell) and "Clear all". Renders
 * nothing when the dashboard has neither, so its strip collapses. Every
 * change goes through the store, which marks the affected tiles stale.
 */
const FilterBar: React.FC = () => {
  const filters = useDashboardStore((s) => s.document.filters)
  const codebook = useDashboardStore((s) => s.codebook)
  const selections = useDashboardStore((s) => s.filterSelections)
  const crossFilters = useDashboardStore((s) => s.crossFilters)
  const pages = useDashboardStore((s) => s.document.pages)
  const setFilterSelection = useDashboardStore((s) => s.setFilterSelection)
  const clearCrossFilter = useDashboardStore((s) => s.clearCrossFilter)
  const clearAll = useDashboardStore((s) => s.clearAllViewerState)
  const active = useDashboardStore(selectAnyFilterActive)

  const optionsById = useMemo(
    () =>
      Object.fromEntries(
        filters.map((f) => [
          f.id,
          getBoolChildLabels(codebook, f.variable).map((l) => ({
            value: l,
            label: l,
          })),
        ])
      ),
    [filters, codebook]
  )
  const tileTitles = useMemo(
    () =>
      new Map(
        pages.flatMap((p) => p.tiles.map((t) => [t.id, t.title] as const))
      ),
    [pages]
  )

  const crossEntries = Object.entries(crossFilters)
  if (filters.length === 0 && crossEntries.length === 0) return null

  return (
    <div className="dq-filterbar" role="group" aria-label="Filters">
      {filters.map((f) => {
        const options = optionsById[f.id] ?? []
        // Before the first codebook arrives nothing is known to be missing.
        const missing = codebook !== null && options.length === 0
        // Tiles ignore categories the data does not have (a stale default
        // after a re-import), so the select does not show them either.
        const known = new Set(options.map((o) => o.value))
        const value = (selections[f.id] ?? []).filter(
          (c) => codebook === null || known.has(c)
        )
        const label = f.label || lastSegment(f.variable)
        return (
          <label key={f.id} className="dq-filterbar__item">
            <span>{label}</span>
            <Select
              size="small"
              aria-label={label}
              mode={f.control === "multi" ? "multiple" : undefined}
              allowClear
              maxTagCount="responsive"
              style={{ minWidth: 160 }}
              disabled={missing}
              placeholder={missing ? "Variable not in data" : "All"}
              options={options}
              value={f.control === "multi" ? value : value[0]}
              onChange={(v: string | string[] | undefined | null) =>
                setFilterSelection(f.id, toValues(v))
              }
            />
            {missing && (
              <em className="dq-filterbar__ignored">
                Ignored: variable not in data
              </em>
            )}
          </label>
        )
      })}
      {crossEntries.map(([tileId, cf]) => {
        const categories = cf.categories.join(" or ")
        const text = `${lastSegment(cf.variable)}: ${categories}`
        const from = tileTitles.get(tileId)
        return (
          <Tag
            key={tileId}
            className="dq-filterbar__chip"
            closable
            title={from ? `From ${from}` : undefined}
            closeIcon={
              <button
                type="button"
                className="dq-filterbar__chip-close"
                aria-label={`Remove ${text}`}
              >
                <CloseOutlined />
              </button>
            }
            onClose={(e) => {
              // The store owns the chip list; keep antd from hiding it.
              e.preventDefault()
              clearCrossFilter(tileId)
            }}
          >
            {text}
          </Tag>
        )
      })}
      {active && (
        <button
          type="button"
          className="dq-filterbar__clear"
          onClick={clearAll}
        >
          Clear all
        </button>
      )}
    </div>
  )
}

export default FilterBar
